/**
    * @prop --uic-table-width: manually set the width of the table, defaults to content width or 100% when using the `full-width` attribute/prop
    * @prop --uic-table-cell-height: height of the rows/table cells
    * @prop --uic-table-group-indicator-height: height of the group indicator, needs to be adapted to `--uic-table-cell-height`
    * @prop --uic-table-horizontal-cell-padding: horizontal padding of the table cells
    */
.uic-table {
    display: block;
    position: relative;

    &__scroller {
        position: absolute;
        width: 0;
        left: 0;
    }

    &__table {
        border-collapse: collapse;
        border-spacing: 0;
        margin: 0 auto;
        width: var(--uic-table-width, max-content);
    }

    &__td {
        height: var(--uic-table-cell-height);
        text-align: left;
        white-space: normal;
        font-size: var(--uic-font-size-normal);
        text-transform: none;
    }

    &__td--multiline {
        /* These are technically the same, but use both */
        overflow-wrap: break-word;
        word-wrap: break-word;

        -ms-word-break: break-all;
        /* This is the dangerous one in WebKit, as it breaks things wherever */
        word-break: break-all;
        /* Instead use this non-standard one: */
        word-break: break-word;

        /* Adds a hyphen where the word breaks, if supported (No Blink) */
        hyphens: auto;
    }

    &__th {
        height: var(--uic-table-cell-height);
        padding-left: var(--uic-table-horizontal-cell-padding);
        padding-right: var(--uic-table-horizontal-cell-padding);
        text-align: left;
        white-space: normal;
    }

    &__cell-container {
        padding-left: var(--uic-table-horizontal-cell-padding);
        padding-right: var(--uic-table-horizontal-cell-padding);
        display: flex;
        justify-content: flex-start;
        align-items: center;
        position: relative;

        &--no-data::before {
            content: var(--uic-table-cell-empty-content, "–");
        }

        &--group {
            height: var(--uic-table-group-indicator-height);
            border-left: 1px solid #a1a1a1;
        }
    }

    &__th {
        color: var(--uic-color-black);
        font-size: var(--uic-font-size-small);
        line-height: var(--uic-line-height-normal);
        font-weight: var(--uic-font-weight-normal);
        background-color: var(--uic-color-white);

        &--group {
            position: relative;
            background: transparent;

            &::after {
                content: "";
                top: 10%;
                position: absolute;
                left: 1px;
                height: 80%;
                width: 1px;
                transform: translate(-50%);
                background-color: var(--uic-color-grey2);
            }
        }
    }

    &__tr .uic-table__td,
    &__tr .uic-table__th {
        background-color: var(--uic-color-white);
    }

    &__tr {
        border-bottom: 1px solid var(--uic-color-grey2);
        border-top: 1px solid var(--uic-color-grey2);
    }

    &__tr-head {
        border-top: 0;
    }

    &__tr:active .uic-table__td {
        background: rgba(185, 220, 247, 0.4);
        color: var(--uic-color-blue3);
        border-top: 1px solid var(--uic-color-blue3);
        border-bottom: 1px solid var(--uic-color-blue3);

        .uic-table__cell--no-data::before {
            background-color: var(--uic-color-blue3);
        }
    }

    &__tr--selected .uic-table__td {
        background: rgba(185, 220, 247, 0.4);
        color: var(--uic-color-blue2);
        border-top: 1px solid var(--uic-color-blue3);
        border-bottom: 1px solid var(--uic-color-blue3);

        .uic-table__cell--no-data::before {
            background-color: var(--uic-color-blue3);
        }
    }

    &__tr--selected .uic-table__cell--no-data {
        &::before {
            background-color: var(--uic-color-blue2);
        }
    }

    &__tr:hover .uic-table__td--no-data {
        &::before {
            background-color: var(--uic-color-blue2);
        }
    }

    &__thead .uic-table__tr {
        background-color: var(--uic-color-white);
        border-top: 0;
        border-bottom: 0;
    }

    &__tr:hover {
        cursor: pointer;
        color: var(--uic-color-blue3);

        .uic-table__td {
            border-bottom: 1px solid var(--uic-color-blue3);
            border-top: 1px solid var(--uic-color-blue3);
        }

        .uic-table__cell--no-data::before {
            background-color: var(--uic-color-blue3);
        }
    }

    &__inner {
        overflow-x: auto;
        height: auto;
    }

    &--full-width {
        width: var(--uic-table-width, 100%);

        table {
            min-width: var(--uic-table-width, 100%);
            width: var(--uic-table-width, 100%);
        }
    }

    &--sticky-thead {
        height: 100%;
        max-height: 100%;
        overflow: scroll;

        .uic-table__inner {
            height: 100%;
        }

        .uic-table__thead {
            position: sticky;
            z-index: 1;
            top: 0;
            background: var(--uic-color-white);
        }

        .uic-table__thead::after {
            content: "";
            display: block;
            position: absolute;
            width: 100%;
            left: 0;
            bottom: -1px;
            height: 1px;
            background: var(--uic-color-grey2);
        }
    }

    .uic-checkbox__mark {
        background: white;
    }
}
